import React, { useState, useRef, useEffect } from "react"
import InterviewEmailSettings from "ATSComponents/Companies/InterviewEmailSettings"
import { Col, Container, Row } from "reactstrap"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import "primereact/resources/themes/saga-blue/theme.css"
import "primereact/resources/primereact.min.css"
import "primeicons/primeicons.css"
import { Dialog } from "primereact/dialog"
import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"
import { Dropdown } from "primereact/dropdown"
import { BreadCrumb } from "primereact/breadcrumb"
import { Editor } from "primereact/editor"
import { Controller, useForm } from "react-hook-form"
import { Link, useLocation } from "react-router-dom"
import axios from "axios"
import { mapAtsUsersToOptions } from "../../utils/userOptions"
import { setPageData } from "store/actions"
import Select from "react-select"
import { useDispatch, useSelector } from "react-redux"
import Modal from "react-bootstrap/Modal"
import Swal from "sweetalert2"
import CompanySelector from "ATSComponents/Common/CompanySelector" // adjust path as needed
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer"

// ─── Signature name validator ─────────────────────────────────────────────────
function validateSignatureName(name, existingNames = []) {
  const errors = []
  if (!name || name.trim().length === 0) {
    errors.push("Signature name is required.")
    return errors
  }
  const trimmed = name.trim()
  if (trimmed.length > 32)
    errors.push("Signature name must be at most 32 characters long.")
  if (name !== trimmed)
    errors.push("Signature name must not start or end with a space.")
  const invalidChars = /[\\/:*?"<>|]/
  if (invalidChars.test(trimmed))
    errors.push("Signature name contains invalid character.")
  if (/^\.+$/.test(trimmed) || trimmed.endsWith("."))
    errors.push(
      "Signature name cannot consist only of periods or end with a dot."
    )
  const reserved = ["default", "none", "system"]
  if (reserved.includes(trimmed.toLowerCase()))
    errors.push("Signature name cannot be a reserved word.")
  if (existingNames.some(n => n.toLowerCase() === trimmed.toLowerCase()))
    errors.push("Signature name already exists.")
  return errors
}

// ─── Tab styles ───────────────────────────────────────────────────────────────
const tabStyles = {
  tabContainer: {
    display: "flex",
    borderBottom: "2px solid #dee2e6",
    marginBottom: "20px",
    gap: "4px",
  },
  tab: isActive => ({
    padding: "10px 22px",
    cursor: "pointer",
    border: "none",
    background: isActive ? "#fff" : "transparent",
    borderBottom: isActive ? "2px solid #0d6efd" : "2px solid transparent",
    color: isActive ? "#0d6efd" : "#6c757d",
    fontWeight: isActive ? "600" : "400",
    fontSize: "14px",
    transition: "all 0.2s ease",
    marginBottom: "-2px",
    borderRadius: "4px 4px 0 0",
  }),
}
const formatVitelDateTime = value => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "numeric", minute: "2-digit", hour12: true,
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// SmtpForm — reusable for both tabs
//
// Props:
//   mode             : "user" | "company"
//   smtpAccounts     : array of rows to show in the table
//   selectedCompanyId: company_id filter value (null = all); passed from parent
//   loading, first, rows, totalRecords, dispatch, getAllActive, type
//   atsUserOptions, isAtsUsersLoading  (user-mode only)
//   companyOptions, isCompaniesLoading (company-mode only, for the dialog dropdown)
// ─────────────────────────────────────────────────────────────────────────────
const SmtpForm = ({
  mode,
  smtpAccounts,
  selectedCompanyId, // ← from parent CompanySelector
  first,
  rows,
  totalRecords,
  dispatch,
  getAllActive,
  type,
  atsUserOptions,
  isAtsUsersLoading,
  companyOptions,
  isCompaniesLoading,
  onBack,
}) => {
  const dt = useRef(null)
  const [displayDialog, setDisplayDialog] = useState(false)
  const [smShow, setSmShow] = useState(false)
  const [successAlert, setSuccessAlert] = useState(false)
  const [smShowcreate, setSmShowcreate] = useState(false)
  const [smcreate, setsmcreate] = useState("Added")
  const [deleteMessage, setDeleteMessage] = useState("Delete Successfully")
  const [isEdited, setIsEdited] = useState(false)
  const [editId, setEditId] = useState(null)
  const [deleteRowId, setDeleteRowID] = useState(null)
  const [smtpslectnames, setsmtpslectnames] = useState("Add SMTP Account")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState("")
  const [saving, setSaving] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    control,
    setValue,
    reset,
  } = useForm()
  const useSSL = watch("UseSSL")
  const useTLS = watch("UseTLS")
  const togglePassword = () => setShowPassword(prev => !prev)

  // API base differs by mode
  const apiBase =
    mode === "user" ? "/api/v1/smtp-accounts/" : "/api/v1/company-smtp/"

  // Build query string — include company_id filter when present
  const buildUrl = (page, size) => {
    let url = `${apiBase}?page=${page}&size=${size}`
    if (selectedCompanyId) url += `&company_id=${selectedCompanyId}`
    return url
  }

  const fetchAll = async (page, size) => {
    if (!selectedCompanyId) {
      getAllActive([], 0)
      return
    }
    try {
      setLoading(true)
      setLoadError("")

      let url = `${apiBase}?page=${1}&size=${100}`
      if (selectedCompanyId) {
        url += `&company_id=${selectedCompanyId}`
      }

      const response = await axios.get(url)

      getAllActive(response.data.results || [], response.data.count || 0)
    } catch (e) {
      console.error(e)
      setLoadError(e.response?.data?.detail || e.response?.data?.message || "Unable to load SMTP accounts.")
      getAllActive([], 0)
    } finally {
      setLoading(false)
    }
  }

  // Re-fetch whenever the company filter changes
  useEffect(() => {
    fetchAll(Math.floor(first / rows) + 1, rows)
  }, [selectedCompanyId])
  const [loadingMailId, setLoadingMailId] = useState(null);
  const handleSendTestMail = async rowData => {
    try {
      setLoadingMailId(rowData.smtp_id || rowData.company_smtp_id); 
      const payload = {
        company_id: rowData.company_id ?? rowData.company ?? selectedCompanyId,
        outgoing_server: rowData.outgoing_server,
        port: rowData.port,
        username: rowData.username,
        password: rowData.password || rowData.watch_word,
        from_email: rowData.from_email,
        to_email: rowData.from_email,
        use_tls: !!rowData.use_tls,
        use_ssl: !!rowData.use_ssl,
      }

      const response = await axios.post("/api/v1/company-smtp/test/", payload)

      if (response?.data?.message) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message || "Test email sent successfully!",
          confirmButtonText: "OK",
        })
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error?.response?.data?.error || "Failed to send test email.",
        confirmButtonText: "OK",
      })
    }
    finally {
      setLoadingMailId(null);
  }
  }

  const onloadrecords = data => {
    const resolvedAtsUserId =
      data?.user_id ?? data?.ats_user_id ?? data?.user?.id ?? data.user ?? null
    setValue("outgoingServer", data.outgoing_server)
    setValue("username", data.username)
    setValue("password", data.watch_word)
    if (mode === "user") setValue("atsUserId", resolvedAtsUserId)
    if (mode === "company")
      setValue("companyId", data.company_id ?? data.company ?? null)
    setValue("Isprimary", data.is_primary)
    setValue("fromEmail", data.from_email)
    setValue("address", data.reply_to_email)
    setValue("UseSSL", data.use_ssl)
    setValue("UseTLS", data.use_tls)
    // company-mode extra fields
    if (mode === "company") {
      setValue("IsDefault", data.is_default ?? false)
      setValue("IsActive", data.is_active ?? true)
    }
    setTimeout(() => {
      setValue("port", data.port)
    }, 1000)
  }
  const formatBool = (value, mode) => {
    if (mode === "user") {
      return value ? "True" : "False" // SMTP (string)
    }
    return !!value // Company SMTP (boolean)
  }
  const onSubmit = async data => {
    setSaving(true)
    let req = {}

    if (mode === "user") {
      // User Based SMTP — simplified: only username, password, from, user
     req = {
      username: data.username,
      password: data.password,
      from_email: data.fromEmail,
      user: data.atsUserId || null,
      company: data.dialogCompanyId || selectedCompanyId || null, // ✅ always in payload
    }
    } else {
      // Company Based SMTP — all fields for both Add and Edit
      req = {
        outgoing_server: data.outgoingServer,
        port: data.port,
        username: data.username,
        password: data.password,
        from_email: data.fromEmail,
        reply_to_email: data.address,
        use_ssl: formatBool(data.UseSSL, mode),
        use_tls: formatBool(data.UseTLS, mode),
        is_primary: formatBool(data.Isprimary, mode),
        is_default: formatBool(data.IsDefault, mode),
      }
      req.company_id = data.companyId || selectedCompanyId || null
    }

    try {
      if (!isEdited) {
        await axios.post(apiBase, req)
        setDisplayDialog(false)
        fetchAll(Math.floor(first / rows) + 1, rows)
        setsmcreate("SMTP Account Added")
        setSmShowcreate(true)
      } else {
        await axios.patch(`${apiBase}${editId}/`, req)
        setDisplayDialog(false)
        setSmShowcreate(true)
        setsmcreate("SMTP Account Updated")
        fetchAll(Math.floor(first / rows) + 1, rows)
      }
    } catch (error) {
      if (error.response?.data) {
        const msg =
          error.response.data.from_email?.[0] ||
          error.response.data.port?.[0] ||
          error.response.data.company_id?.[0] ||
          error.response.data.error ||
          error.response.data.detail ||
          "An unexpected error occurred."
          Swal.fire({
            icon: "error",
            title: "Error",
            text: msg,
            confirmButtonText: "OK",
            customClass: {
              container: "swal-on-top",
            },
          })
      }
    } finally {
      setSaving(false)
    }
  }
  const deleteHandler = async () => {
    try {
      await axios.delete(`${apiBase}${deleteRowId}/`)
      fetchAll(Math.floor(first / rows) + 1, rows)
      setSmShow(false)
      setDeleteMessage("SMTP Account Deleted")
      setSuccessAlert(true)
    } catch (error) {
      console.error("Delete error:", error)
    }
  }

  const closeDialog = () => {
    setDisplayDialog(false)
    setSmShow(false)
    setSuccessAlert(false)
  }

  return (
    <div>
      {/* ── Add Button ── */}
      <Row>
        <Col lg={12}>
          <h6 className="mb-1">SMTP Email Accounts</h6>
          <Row className="mb-1">
            <Col lg={12} xs={12}>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  onClick={() => {
                    reset()
                    if (mode === "user") {
                      setValue("atsUserId", null)
                      setValue("dialogCompanyId", selectedCompanyId || null)
                    }
                    if (mode === "company") {
                      setValue("companyId", selectedCompanyId || null)
                    }
                    setsmcreate("Add")
                    setsmtpslectnames("Add SMTP Account")
                    setDisplayDialog(true)
                    setIsEdited(false)
                  }}
                  color="primary"
                  className="btn btn-primary outlinebtn d-block text-white"
                >
                  <i className="pi pi-plus me-1"></i>Add SMTP
                </Button>
                <Button
                  type="button"
                  onClick={onBack}
                  className="btn btn-secondary d-block text-white"
                >
                  <i className="pi pi-arrow-left me-1"></i>Back
                </Button>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* ── Data Table ── */}
      <Row>
        <Col style={{ marginBottom: "80px" }}>
          <div id="Smtp_table" className="smtp-table-shell">
            {loading ? (
              <div className="p-3">
                <OrgTableShimmer columns={8} rows={8} titleWidth="230px" />
              </div>
            ) : loadError ? (
              <div className="p-4 text-danger" role="alert">{loadError}</div>
            ) : (
              <DataTable
                ref={dt}
                value={smtpAccounts}
                loading={false} // IMPORTANT: disable internal loader
                dataKey="smtp_id"
                // scrollHeight="500px"
                showGridlines
                emptyMessage="No SMTP accounts found."
                //   lazy
                //   paginator
                //   first={first}
                //   rows={rows}
                //   totalRecords={totalRecords}
                //   onPage={(e) => {
                //     const newPage = Math.floor(e.first / e.rows) + 1
                //     dispatch(setPageData({ first: e.first, rows: e.rows, totalRecords }))
                //     fetchAll(newPage, e.rows)
                //   }}
                //   rowsPerPageOptions={[10, 25, 50]}
                //   paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                //   currentPageReportTemplate="{first} to {last} of {totalRecords}"
              >
                <Column field="username" header="Username" sortable />
                <Column field="from_email" header="From Email" sortable />
                <Column field="reply_to_email" header="Reply Address" sortable />
                <Column field="created_by_name" header="Created By" sortable body={rowData => rowData.created_by_name || "—"} />
                <Column field="created_at" header="Created At" sortable body={rowData => formatVitelDateTime(rowData.created_at)} />
                <Column field="updated_by_name" header="Updated By" sortable body={rowData => rowData.updated_by_name || "—"} />
                <Column field="updated_at" header="Updated At" sortable body={rowData => formatVitelDateTime(rowData.updated_at)} />
                <Column
                  header="Action"
                  body={rowData => (
                    <div className="smtp-actions-cell">
                      {/* ✏️ Edit */}
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-success p-button-sm"
                        onClick={() => {
                          setIsEdited(true)
                          setEditId(rowData.smtp_id || rowData.company_smtp_id)
                          setsmcreate("Update")
                          setsmtpslectnames("Edit SMTP Account")
                          setDisplayDialog(true)
                          onloadrecords(rowData)
                        }}
                        tooltip="Edit"
                        tooltipOptions={{ position: "top" }}
                      />

                      {/* 🧪 Test Mail */}

                      {/* 🗑️ Delete */}
                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger p-button-sm"
                        onClick={() => {
                          setDeleteRowID(rowData.smtp_id || rowData.company_smtp_id)
                          setSmShow(true)
                        }}
                        tooltip="Delete"
                        tooltipOptions={{ position: "top" }}
                      />
                      <Button
                        icon="pi pi-send"
                        className="p-button-rounded p-button-info p-button-sm"
                        onClick={() => handleSendTestMail(rowData)}
                        tooltip="Send Test Mail"
                        loading={loadingMailId === rowData.smtp_id || loadingMailId === rowData.company_smtp_id}
                        disabled={loadingMailId === rowData.smtp_id || loadingMailId === rowData.company_smtp_id}
                        tooltipOptions={{ position: "top" }}
                      />
                    </div>
                  )}
                />
              </DataTable>
            )}
          </div>
        </Col>
      </Row>

      {/* ── Add / Edit Dialog ── */}
      <Dialog
        header={smtpslectnames}
        visible={displayDialog}
        onHide={closeDialog}
        style={{ width: "60vw", overflow: "visible" }}
        contentStyle={{ overflow: "visible" }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="card p-4 pt-0 pb-4 mt-2" style={{ overflow: "visible" }}>
            <h6 className="mb-2 mt-4">SMTP Account</h6>

            {mode === "user" && (
              <>
                <input type="hidden" {...register("atsUserId")} />
                <input type="hidden" {...register("dialogCompanyId")} />
              </>
            )}
            {mode === "company" && (
              <input type="hidden" {...register("companyId")} />
            )}

            {/* ══════════════════════════════════════════════
                USER BASED SMTP — Simplified fields only
            ══════════════════════════════════════════════ */}
            {mode === "user" && (
              <>
                <Row>
                  <Col lg={6}>
                    <div className="p-field mb-2">
                      <label>
                        Select Company<span className="text-danger">*</span>
                      </label>
                      <CompanySelector
                        selectedCompanyId={selectedCompanyId}
                        onCompanyChange={c => {
                          const compId =
                            c?.company_id === "All"
                              ? null
                              : c?.company_id ?? null
                          setValue("dialogCompanyId", compId)
                        }}
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="p-field mb-2" style={{ position: "relative", zIndex: 10 }}>
                      <label>Select User</label>
                      <Controller
                        name="atsUserId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={atsUserOptions}
                            isLoading={isAtsUsersLoading}
                            placeholder={
                              isAtsUsersLoading
                                ? "Loading users..."
                                : "Select user"
                            }
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                            }}
                            value={
                              atsUserOptions.find(
                                opt => opt.value === field.value
                              ) || null
                            }
                            onChange={selected =>
                              field.onChange(selected ? selected.value : null)
                            }
                          />
                        )}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col lg={6}>
                    <div className="p-field mb-2">
                      <label>
                        Username<span className="text-danger">*</span>
                      </label>
                      <InputText
                        placeholder="User name"
                        className="w-full"
                        {...register("username", {
                          required: "Username is required",
                        })}
                      />
                      {errors.username && (
                        <small className="form-text text-danger">
                          {errors.username.message}
                        </small>
                      )}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="p-field mb-2">
                      <label>
                        Password<span className="text-danger">*</span>
                      </label>
                      <div className="p-inputgroup">
                        <InputText
                          placeholder="Password"
                          className="w-full"
                          type={showPassword ? "text" : "password"}
                          {...register("password", {
                            required: "Password is required",
                          })}
                          onKeyUp={() => trigger("password")}
                        />
                        <Button
                          icon={showPassword ? "pi pi-eye" : "pi pi-eye-slash"}
                          onClick={togglePassword}
                          className="p-button-outlined"
                          type="button"
                        />
                      </div>
                      {errors.password && (
                        <small className="form-text text-danger">
                          {errors.password.message}
                        </small>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col lg={6}>
                    <div className="p-field mb-2">
                      <label>
                        From<span className="text-danger">*</span>
                      </label>
                      <InputText
                        placeholder="e.g., no-reply@example.com"
                        className="w-full"
                        {...register("fromEmail", {
                          required: "From Email is required",
                        })}
                        onKeyUp={() => trigger("fromEmail")}
                      />
                      {errors.fromEmail && (
                        <small className="form-text text-danger">
                          {errors.fromEmail.message}
                        </small>
                      )}
                    </div>
                  </Col>
                </Row>
              </>
            )}

            {/* ══════════════════════════════════════════════
                COMPANY BASED SMTP — All fields
            ══════════════════════════════════════════════ */}
            {mode === "company" && (
              <>
                <Row>
                  <Col lg={9}>
                    <div className="p-field mb-2">
                      <label>
                        Outgoing Server<span className="text-danger">*</span>
                      </label>
                      <InputText
                        placeholder="e.g., smtp.example.com"
                        className="w-full"
                        {...register("outgoingServer", {
                          required: "Outgoing Server is required",
                          pattern: {
                            value:
                              /^(?:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/,
                            message: "Please enter a valid Outgoing Server",
                          },
                        })}
                        onKeyUp={() => trigger("outgoingServer")}
                      />
                      {errors.outgoingServer && (
                        <small className="form-text text-danger">
                          {errors.outgoingServer.message}
                        </small>
                      )}
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="p-field mb-2">
                      <label>
                        Port<span className="text-danger">*</span>
                      </label>
                      <InputText
                        placeholder="Enter port number"
                        type="number"
                        className="w-full"
                        {...register("port", { required: "Port is required" })}
                        onKeyUp={() => trigger("port")}
                      />
                      {errors.port && (
                        <small className="form-text text-danger">
                          {errors.port.message}
                        </small>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col lg={6}>
                    <div className="p-field mb-2">
                      <label>
                        Username<span className="text-danger">*</span>
                      </label>
                      <InputText
                        placeholder="User name"
                        className="w-full"
                        {...register("username", {
                          required: "Username is required",
                        })}
                      />
                      {errors.username && (
                        <small className="form-text text-danger">
                          {errors.username.message}
                        </small>
                      )}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="p-field mb-2">
                      <label>
                        Password<span className="text-danger">*</span>
                      </label>
                      <div className="p-inputgroup">
                        <InputText
                          placeholder="Password"
                          className="w-full"
                          type={showPassword ? "text" : "password"}
                          {...register("password", {
                            required: "Password is required",
                          })}
                          onKeyUp={() => trigger("password")}
                        />
                        <Button
                          icon={showPassword ? "pi pi-eye" : "pi pi-eye-slash"}
                          onClick={togglePassword}
                          className="p-button-outlined"
                          type="button"
                        />
                      </div>
                      {errors.password && (
                        <small className="form-text text-danger">
                          {errors.password.message}
                        </small>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col lg={6}>
                    <div className="p-field mb-2">
                      <label>
                        From<span className="text-danger">*</span>
                      </label>
                      <InputText
                        placeholder="e.g., no-reply@example.com"
                        className="w-full"
                        {...register("fromEmail", {
                          required: "From Email is required",
                        })}
                        onKeyUp={() => trigger("fromEmail")}
                      />
                      {errors.fromEmail && (
                        <small className="form-text text-danger">
                          {errors.fromEmail.message}
                        </small>
                      )}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="p-field">
                      <label>
                        Reply Address Email<span className="text-danger">*</span>
                      </label>
                      <InputText
                        placeholder="Enter reply address"
                        className="w-full"
                        {...register("address", {
                          required: "Reply Address Email is required",
                        })}
                        onKeyUp={() => trigger("address")}
                      />
                      {errors.address && (
                        <small className="form-text text-danger">
                          {errors.address.message}
                        </small>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row className="d-flex align-items-center mt-2">
                  <Col lg={2}>
                    <div className="UseSSL">
                      <input
                        type="checkbox"
                        {...register("UseSSL")}
                        disabled={useTLS}
                        className="me-2"
                      />
                      <label className="mb-0">Use SSL</label>
                    </div>
                  </Col>
                  <Col lg={2}>
                    <div className="UseSSL">
                      <input
                        type="checkbox"
                        {...register("UseTLS")}
                        disabled={useSSL}
                        className="me-2"
                      />
                      <label className="mb-0">Use TLS</label>
                    </div>
                  </Col>
                  <Col lg={2}>
                    <div className="UseSSL">
                      <input
                        type="checkbox"
                        {...register("Isprimary")}
                        className="me-2"
                      />
                      <label className="mb-0">Is Primary</label>
                    </div>
                  </Col>
                  <Col lg={2}>
                    <div className="UseSSL">
                      <input
                        type="checkbox"
                        {...register("IsDefault")}
                        className="me-2"
                      />
                      <label className="mb-0">Is Default</label>
                    </div>
                  </Col>
                </Row>
              </>
            )}

            <Row className="align-items-center mt-3">
              <Col md={12}>
                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary btn-main me-2"
                    disabled={saving}
                  >
                    {saving ? <i className="pi pi-spin pi-spinner me-1" /> : <i className="pi pi-check me-1"></i>}
                    {saving ? (isEdited ? "Updating..." : "Adding...") : (isEdited ? "Update" : "Add")}
                  </button>
                </div>
              </Col>
            </Row>
          </div>
        </form>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        header="Delete SMTP"
        visible={smShow}
        onHide={closeDialog}
        style={{ width: "30vw" }}
      >
        <h4 className="text-center">Are you sure you want to delete?</h4>
        <div className="text-center m-3">
          <Button
            className="btn btn-primary m-1"
            onClick={() => setSmShow(false)}
          >
            Cancel
          </Button>
          <button className="btn btn-primary m-1" onClick={deleteHandler}>
            Delete
          </button>
        </div>
      </Dialog>

      {/* Delete Success */}
      <Dialog
        header="Delete Document"
        visible={successAlert}
        onHide={closeDialog}
        style={{ width: "30vw" }}
      >
        <h4 className="text-center">{deleteMessage}</h4>
        <div className="text-center m-3">
          <button
            className="btn btn-primary"
            onClick={() => setSuccessAlert(false)}
          >
            OK
          </button>
        </div>
      </Dialog>

      {/* Create/Update Success */}
      <Modal
        size="md"
        show={smShowcreate}
        centered
        onHide={() => setSmShowcreate(false)}
        className="text-center settings-success-modal"
      >
        <Modal.Header closeButton>
          <h1 className="modal-title fs-5">{smcreate}</h1>
        </Modal.Header>
        <div style={{ textAlign: "center", padding: "10px 16px 0" }}>
          <h4 className="m-0">{smcreate} Successfully</h4>
        </div>
        <div style={{ textAlign: "center", margin: "18px 0" }}>
          <Button
            className="btn btn-primary m-1"
            onClick={() => setSmShowcreate(false)}
          >
            OK
          </Button>
        </div>
      </Modal>
    </div>
  )
}
const VitelMeetForm = ({
  selectedCompanyId,
  onBack,
  atsUserOptions = [],
  isAtsUsersLoading = false,
  fetchAtsUsers = async () => [],
}) => {
  const dt = useRef(null)
  const [vitelAccounts, setVitelAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState("")
  const [displayDialog, setDisplayDialog] = useState(false)
  const [smShow, setSmShow] = useState(false)
  const [successAlert, setSuccessAlert] = useState(false)
  const [smShowcreate, setSmShowcreate] = useState(false)
  const [smcreate, setsmcreate] = useState("Added")
  const [deleteMessage, setDeleteMessage] = useState("Delete Successfully")
  const [isEdited, setIsEdited] = useState(false)
  const [editId, setEditId] = useState(null)
  const [deleteRowId, setDeleteRowID] = useState(null)
  const [vitelSelectName, setVitelSelectName] = useState("Add Vitel Credentials")
  const [showApiKey, setShowApiKey] = useState(false)
  const [showApiSecret, setShowApiSecret] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testingId, setTestingId] = useState(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    control,
    setValue,
    reset,
  } = useForm()

  const isActive = watch("is_active")
  const apiBase = "/api/v1/video-meeting-credentials/"

  const fetchAll = async () => {
    if (!selectedCompanyId) {
      setVitelAccounts([])
      setLoadError("")
      return
    }
    try {
      setLoading(true)
      setLoadError("")
      const response = await axios.get(`${apiBase}?company_id=${selectedCompanyId}`)
      const data = Array.isArray(response.data)
        ? response.data
        : response.data.results || []
      setVitelAccounts(data)
    } catch (e) {
      console.error(e)
      setLoadError(e.response?.data?.detail || e.response?.data?.message || "Unable to load Vitel Meet credentials.")
      setVitelAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [selectedCompanyId])

  const onloadrecords = data => {
    setValue("provider", data.provider || "vitel")
    setValue("api_key", data.api_key)
    setValue("api_secret", data.api_secret)
    setValue("is_active", data.is_active ?? true)
    setValue("user", data.user || null)
  }

  const testEndpoint = async (apiKey, apiSecret, rowId = null, notify = false) => {
    if (!String(apiKey || "").trim() || !String(apiSecret || "").trim()) {
      const message = "API Key and API Secret are required."
      if (notify) Swal.fire({ icon: "error", title: "Unable to test endpoint", text: message })
      return
    }
    setTesting(true)
    setTestingId(rowId || "form")
    try {
      const response = await axios.post(`${apiBase}test/`, {
        api_key: apiKey,
        api_secret: apiSecret,
      })
      const message = response.data?.message || "Vitel Meet endpoint connection successful"
      if (notify) Swal.fire({ icon: "success", title: "Connection successful", text: message })
    } catch (error) {
      const responseData = error.response?.data
      const message = responseData?.error || responseData?.detail || responseData?.message || "Unable to connect to the Vitel Meet endpoint."
      if (notify) Swal.fire({ icon: "error", title: "Endpoint test failed", text: message })
    } finally {
      setTesting(false)
      setTestingId(null)
    }
  }

  const onSubmit = async data => {
    setSaving(true)
    const req = {
      company: selectedCompanyId,
      user: data.user || null,
      provider: data.provider || "vitel",
      api_key: data.api_key,
      api_secret: data.api_secret,
      is_active: !!data.is_active,
    }

    try {
      if (!isEdited) {
        await axios.post(apiBase, req)
        setDisplayDialog(false)
        setsmcreate("Vitel Credentials Added")
        setSmShowcreate(true)
        fetchAll()
      } else {
        await axios.patch(`${apiBase}${editId}/`, req)
        setDisplayDialog(false)
        setsmcreate("Vitel Credentials Updated")
        setSmShowcreate(true)
        fetchAll()
      }
    } catch (error) {
      if (error.response?.data) {
        const msg =
          error.response.data.api_key?.[0] ||
          error.response.data.api_secret?.[0] ||
          error.response.data.company?.[0] ||
          error.response.data.error ||
          error.response.data.detail ||
          "An unexpected error occurred."
          Swal.fire({
              icon: "error",
              title: "Error",
              text: error?.response?.data?.error || "Failed to Add Credentials.",
              confirmButtonText: "OK",
              customClass: {
                container: "swal-on-top",
              },
            })
      }
    } finally {
      setSaving(false)
    }
  }

  const deleteHandler = async () => {
    try {
      await axios.delete(`${apiBase}${deleteRowId}/`)
      fetchAll()
      setSmShow(false)
      setDeleteMessage("Vitel Credentials Deleted")
      setSuccessAlert(true)
    } catch (error) {
      console.error("Delete error:", error)
    }
  }

  const closeDialog = () => {
    setDisplayDialog(false)
    setSmShow(false)
    setSuccessAlert(false)
  }

  const maskValue = value =>
    value ? `${"•".repeat(Math.max(value.length - 4, 4))}${value.slice(-4)}` : ""

  return (
    <div>
      <Row>
        <Col lg={12}>
          <h6 className="mb-1">Vitel Meet Credentials</h6>
          <Row className="mb-1">
            <Col lg={12} xs={12}>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  onClick={async () => {
                    if (selectedCompanyId) {
                      await fetchAtsUsers(selectedCompanyId)
                    }
                    reset({
                      provider: "vitel",
                      user: null,
                      api_key: "",
                      api_secret: "",
                      is_active: true,
                    })
                    setsmcreate("Add")
                    setVitelSelectName("Add Vitel Credentials")
                    setDisplayDialog(true)
                    setIsEdited(false)
                  }}
                  color="primary"
                  className="btn btn-primary outlinebtn d-block text-white"
                  disabled={!selectedCompanyId}
                >
                  <i className="pi pi-plus me-1"></i>Add Vitel Credentials
                </Button>
                <Button
                  type="button"
                  onClick={onBack}
                  className="btn btn-secondary d-block text-white"
                >
                  <i className="pi pi-arrow-left me-1"></i>Back
                </Button>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col style={{ marginBottom: "80px" }}>
          <div id="Vitel_table" className="vitel-table-shell">
            {loading ? (
              <div className="p-3">
                <OrgTableShimmer columns={8} rows={8} titleWidth="230px" />
              </div>
            ) : loadError ? (
              <div className="p-4 text-danger" role="alert">{loadError}</div>
            ) : (
              <div id="VitelMeetLink">
              <DataTable
                ref={dt}
                value={vitelAccounts}
                loading={false}
                dataKey="id"
                showGridlines
                emptyMessage="No Vitel Meet credentials found."
              >
                <Column field="provider" header="Provider" sortable />
                <Column
                  field="user_name"
                  header="User Name"
                  sortable
                  body={rowData => (
                    <span title={rowData.user_name || "-"}>{rowData.user_name || "-"}</span>
                  )}
                />
                <Column
                  field="user_email"
                  header="User Email"
                  sortable
                  body={rowData => (
                    <span title={rowData.user_email || "-"}>{rowData.user_email || "-"}</span>
                  )}
                />
                <Column
                  field="api_key"
                  header="API Key"
                  sortable
                  body={rowData => maskValue(rowData.api_key)}
                />
                {/* <Column
                  field="api_secret"
                  header="API Secret"
                  body={rowData => maskValue(rowData.api_secret)}
                /> */}
                <Column
                  field="is_active"
                  header="Active"
                  sortable
                  body={rowData => (
                    <span
                      className={`badge ${
                        rowData.is_active ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {rowData.is_active ? "Active" : "Inactive"}
                    </span>
                  )}
                />
                <Column
                  field="created_at"
                  header="Created At"
                  sortable
                  body={rowData => formatVitelDateTime(rowData.created_at)}
                />
                <Column
                  field="created_by_name"
                  header="Created By"
                  sortable
                  body={rowData => rowData.created_by_name || "—"}
                />
                <Column
                  field="updated_at"
                  header="Updated At"
                  sortable
                  body={rowData => formatVitelDateTime(rowData.updated_at)}
                />
                <Column
                  field="updated_by_name"
                  header="Updated By"
                  sortable
                  body={rowData => rowData.updated_by_name || "—"}
                />
                <Column
                  header="Action"
                  body={rowData => (
                    <div className="smtp-actions-cell">
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-success p-button-sm"
                        onClick={() => {
                          setIsEdited(true)
                          setEditId(rowData.id)
                          setsmcreate("Update")
                          setVitelSelectName("Edit Vitel Credentials")
                          setDisplayDialog(true)
                          onloadrecords(rowData)
                        }}
                        tooltip="Edit"
                        tooltipOptions={{ position: "top" }}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger p-button-sm"
                        onClick={() => {
                          setDeleteRowID(rowData.id)
                          setSmShow(true)
                        }}
                        tooltip="Delete"
                        tooltipOptions={{ position: "top" }}
                      />
                      <Button
                        icon={testing && testingId === rowData.id ? "pi pi-spin pi-spinner" : "pi pi-bolt"}
                        className="p-button-rounded p-button-warning p-button-sm"
                        onClick={() => testEndpoint(rowData.api_key, rowData.api_secret, rowData.id, true)}
                        tooltip="Test Endpoint"
                        tooltipOptions={{ position: "top" }}
                        disabled={testing}
                      />
                    </div>
                  )}
                />
              </DataTable>
            </div>
            )}
          </div>
        </Col>
      </Row>
      <Dialog
        header={vitelSelectName}
        visible={displayDialog}
        onHide={closeDialog}
        style={{ width: "50vw", overflow: "visible" }}
        contentStyle={{ overflow: "visible" }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="card p-4 pt-0 pb-4 mt-2" style={{ overflow: "visible" }}>
            <h6 className="mb-2 mt-4">Vitel Meet Credentials</h6>

            <input type="hidden" {...register("provider")} />

            <Row>
              <Col lg={6}>
                <div className="p-field mb-2" style={{ position: "relative", zIndex: 10 }}>
                  <label>
                    Select User<span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="user"
                    control={control}
                    rules={{ required: "User is required" }}
                    render={({ field }) => (
                      <Select
                        options={atsUserOptions}
                        isLoading={isAtsUsersLoading}
                        isDisabled={isAtsUsersLoading || !selectedCompanyId}
                        placeholder={
                          isAtsUsersLoading ? "Loading users..." : "Select user"
                        }
                        noOptionsMessage={() =>
                          isAtsUsersLoading
                            ? "Loading users..."
                            : selectedCompanyId
                            ? "No users found"
                            : "Select company first"
                        }
                        isClearable
                        isSearchable
                        inputId="vitel-user-select"
                        autoComplete="off"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        value={
                          atsUserOptions.find(opt => opt.value === field.value) ||
                          null
                        }
                        onChange={selected =>
                          field.onChange(selected ? selected.value : null)
                        }
                      />
                    )}
                  />
                  {errors.user && (
                    <small className="form-text text-danger">
                      {errors.user.message}
                    </small>
                  )}
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field mb-2">
                  <label>
                    API Key<span className="text-danger">*</span>
                  </label>
                  <div className="p-inputgroup">
                    <InputText
                      placeholder="API Key"
                      className="w-full"
                      type={showApiKey ? "text" : "password"}
                      {...register("api_key", {
                        required: "API Key is required",
                      })}
                      onKeyUp={() => trigger("api_key")}
                    />
                    <Button
                      icon={showApiKey ? "pi pi-eye" : "pi pi-eye-slash"}
                      onClick={() => setShowApiKey(prev => !prev)}
                      className="p-button-outlined"
                      type="button"
                    />
                  </div>
                  {errors.api_key && (
                    <small className="form-text text-danger">
                      {errors.api_key.message}
                    </small>
                  )}
                </div>
              </Col>
              <Col lg={6}>
                <div className="p-field mb-2">
                  <label>
                    API Secret<span className="text-danger">*</span>
                  </label>
                  <div className="p-inputgroup">
                    <InputText
                      placeholder="API Secret"
                      className="w-full"
                      type={showApiSecret ? "text" : "password"}
                      {...register("api_secret", {
                        required: "API Secret is required",
                      })}
                      onKeyUp={() => trigger("api_secret")}
                    />
                    <Button
                      icon={showApiSecret ? "pi pi-eye" : "pi pi-eye-slash"}
                      onClick={() => setShowApiSecret(prev => !prev)}
                      className="p-button-outlined"
                      type="button"
                    />
                  </div>
                  {errors.api_secret && (
                    <small className="form-text text-danger">
                      {errors.api_secret.message}
                    </small>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col lg={4}>
                <div className="UseSSL">
                  <input
                    type="checkbox"
                    {...register("is_active")}
                    className="me-2"
                  />
                  <label className="mb-0">Is Active</label>
                </div>
              </Col>
            </Row>

            <Row className="align-items-center mt-3">
              <Col md={12}>
                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary btn-main me-2"
                    disabled={saving}
                  >
                    {saving ? <i className="pi pi-spin pi-spinner me-1" /> : <i className="pi pi-check me-1"></i>}
                    {saving ? (isEdited ? "Updating..." : "Adding...") : (isEdited ? "Update" : "Add")}
                  </button>
                </div>
              </Col>
            </Row>
          </div>
        </form>
      </Dialog>
      <Dialog
        header="Delete Vitel Credentials"
        visible={smShow}
        onHide={closeDialog}
        style={{ width: "30vw" }}
      >
        <h4 className="text-center">Are you sure you want to delete?</h4>
        <div className="text-center m-3">
          <Button
            className="btn btn-primary m-1"
            onClick={() => setSmShow(false)}
          >
            Cancel
          </Button>
          <button className="btn btn-primary m-1" onClick={deleteHandler}>
            Delete
          </button>
        </div>
      </Dialog>
      <Dialog
        header="Delete Document"
        visible={successAlert}
        onHide={closeDialog}
        style={{ width: "30vw" }}
      >
        <h4 className="text-center">{deleteMessage}</h4>
        <div className="text-center m-3">
          <button
            className="btn btn-primary"
            onClick={() => setSuccessAlert(false)}
          >
            OK
          </button>
        </div>
      </Dialog>
      <Modal
        size="md"
        show={smShowcreate}
        centered
        onHide={() => setSmShowcreate(false)}
        className="text-center settings-success-modal"
      >
        <Modal.Header closeButton>
          <h1 className="modal-title fs-5">{smcreate}</h1>
        </Modal.Header>
        <div style={{ textAlign: "center", padding: "10px 16px 0" }}>
          <h4 className="m-0">{smcreate} Successfully</h4>
        </div>
        <div style={{ textAlign: "center", margin: "18px 0" }}>
          <Button
            className="btn btn-primary m-1"
            onClick={() => setSmShowcreate(false)}
          >
            OK
          </Button>
        </div>
      </Modal>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Settings Component
// ─────────────────────────────────────────────────────────────────────────────
const Settings = ({ type, embedded = false }) => {
  document.title = "ATS - Admin"
  const dispatch = useDispatch()
  const { first, rows, totalRecords } = useSelector(
    state => state.calendar.pagination
  )
const swalZIndexFix = `
  .swal2-container {
    z-index: 99999 !important;
  }
`
useEffect(() => {
  const styleTag = document.createElement("style")
  styleTag.innerHTML = swalZIndexFix
  document.head.appendChild(styleTag)
  return () => document.head.removeChild(styleTag)
}, [])

  const [selectedOption, setSelectedOption] = useState(null)
  const [pageLoading, setPageLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const initialTabIndex = Number(queryParams.get("selectedTab")) || 0
  const [currentTab, setCurrentTab] = useState(initialTabIndex)

  // ── SMTP tab ──
  const [smtpTab, setSmtpTab] = useState("user")
  const [companyTabCompanyIdVitel, setCompanyTabCompanyIdVitel] = useState(null)

  // ── Company filter state — one per tab ──
  // User-based tab: company_id used as a filter + sent to API
  const [userTabCompanyId, setUserTabCompanyId] = useState(null)
  // Company-based tab: company_id used as a filter + sent to API
  const [companyTabCompanyId, setCompanyTabCompanyId] = useState(null)

  // ── SMTP data ──
  const [userSmtpAccounts, setUserSmtpAccounts] = useState([])
  const [userSmtpTotal, setUserSmtpTotal] = useState(0)
  const [companySmtpAccounts, setCompanySmtpAccounts] = useState([])
  const [companySmtpTotal, setCompanySmtpTotal] = useState(0)

  // ── ATS users (for user-tab dialog) ──
  const [atsUsers, setAtsUsers] = useState([])
  const [isAtsUsersLoading, setIsAtsUsersLoading] = useState(false)

  // ── Companies (for company-tab dialog select) ──
  const [companies, setCompanies] = useState([])
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(false)

  const atsUserOptions = atsUsers.map(user => ({
    value: user.id,
    label: `${user.name || "User"}${user.id ? ` (${user.id})` : ""}`,
  }))

  const companyOptions = companies.map(c => ({
    value: c.id ?? c.company_id,
    label: c.name ?? c.company_name ?? `Company ${c.id}`,
  }))

  const fetchAtsUsers = async (companyId) => {
    if (!companyId) {
      setAtsUsers([])
      return []
    }
    setIsAtsUsersLoading(true)
    try {
      const response = await axios.get(`/api/v1/ats-users/?company_id=${companyId}`)
      const mappedUsers = mapAtsUsersToOptions(response.data)
      setAtsUsers(mappedUsers)
      return mappedUsers
    } catch (error) {
      console.error("Error fetching ATS users:", error)
      setAtsUsers([])
      return []
    } finally {
      setIsAtsUsersLoading(false)
    }
  }
  useEffect(() => {
    setCurrentTab(initialTabIndex)
  }, [initialTabIndex])
  useEffect(() => {
    if (smtpTab === "user" && userTabCompanyId) {
      fetchAtsUsers(userTabCompanyId)
    }
    if (smtpTab === "vitel" && companyTabCompanyIdVitel) {
      fetchAtsUsers(companyTabCompanyIdVitel)
    }
  }, [userTabCompanyId, companyTabCompanyIdVitel, smtpTab])

  // ── Signature state ──
  const [signatures] = useState([{ name: "Default Signature" }])
  const [dialogAction, setDialogAction] = useState("")
  const [signatureName, setSignatureName] = useState("")
  const [selectSign, setSelectSign] = useState("")
  const [signatureOptions, setsignatureOptions] = useState([])
  const [text, setText] = useState("")
  const [sigchecked, setsigchecked] = useState(false)
  const [isEditedsig, setisEditedsig] = useState(false)
  const [editIdsig, seteditIdsig] = useState(null)
  const [errorhandling, seterrorhandling] = useState("")
  const [dialogVisible, setDialogVisible] = useState(false)
  const [smShowcreate, setSmShowcreate] = useState(false)
  const [smShowDeleteSig, setSmShowDeleteSig] = useState(false)
  const [smDeleteSig] = useState("Signature Deleted")
  const [smcreate, setsmcreate] = useState("Added")
  const [isEditing, setIsEditing] = useState(false)

  const {
    register: register1,
    handleSubmit: handleSubmit1,
    reset: reset1,
  } = useForm({ mode: "onBlur" })

  const getsignature = async () => {
    try {
      const response = await axios.get(`/api/v1/email-signatures/`)
      setsignatureOptions(
        response.data.map(cond => ({ name: cond.name, id: cond.signature_id }))
      )
    } catch (error) {}
  }

  const handleDeleteSignature = async id => {
    try {
      const response = await axios.delete(`/api/v1/email-signatures/${id}/`)
      if ([200, 204].includes(response.status)) {
        getsignature()
        setText("")
        setSmShowDeleteSig(true)
      }
    } catch (error) {}
  }

  const handleSelectSignature = async value => {
    setSelectSign(value)
    try {
      const response = await axios.get(`/api/v1/email-signatures/${value.id}/`)
      setText(response.data?.signature_text || "")
      setsigchecked(response.data.is_default)
    } catch (error) {
      setText("")
    }
  }

  const handleSaveSignature = async id => {
    const req = {
      name: selectSign.name,
      signature_text: text,
      is_default: sigchecked ? "true" : "false",
    }
    try {
      const response = await axios.patch(`/api/v1/email-signatures/${id}/`, req)
      if ([200, 204].includes(response.status)) {
        setSmShowcreate(true)
        setSelectSign("")
        setText("")
        setsigchecked("")
      }
    } catch (error) {}
  }

  const onSubmitsignature = async data => {
    let existingNames = signatureOptions.map(s => s.name)
    if (isEditedsig) {
      const currentName = signatureOptions.find(s => s.id === editIdsig)?.name
      existingNames = existingNames.filter(n => n !== currentName)
    }
    const errs = validateSignatureName(signatureName, existingNames)
    if (errs.length > 0) {
      seterrorhandling(errs.join(", "))
      return
    }
    const req = {
      name: signatureName,
      is_default: data.isDefaultSIG ? "true" : "false",
    }
    if (!isEditedsig) {
      try {
        await axios.post(`/api/v1/email-signatures/`, req)
        getsignature()
        setDialogVisible(false)
        setsmcreate("Added New Signature")
        setSmShowcreate(true)
      } catch (error) {
        if (error.response?.data?.error)
          seterrorhandling(error.response.data.error)
      }
    } else {
      try {
        await axios.patch(`/api/v1/email-signatures/${editIdsig}/`, req)
        setDialogVisible(false)
        setsmcreate("Signature Updated")
        setSmShowcreate(true)
        getsignature()
      } catch (error) {
        if (error.response?.data?.error)
          seterrorhandling(error.response.data.error)
      }
    }
  }

  // ── Template state ──
  const [templateOptions, settemplateOptions] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState("")
  const [templateContent, setTemplateContent] = useState("")
  const [isTemplateEditing, setIsTemplateEditing] = useState(false)
  const [isNewTemplateVisible, setNewTemplateVisible] = useState(false)
  const [newTemplateName, setnewTemplateName] = useState("")
  const [editIdtem, seteditIdtem] = useState(null)
  const [isEditedtem, setisEditedtem] = useState(false)
  const [errorhandlingtem, seterrorhandlingtem] = useState("")
  const [displayDialog, setDisplayDialog] = useState(false)
  const [successAlert, setSuccessAlert] = useState(false)
  const [smShow, setSmShow] = useState(false)
  const [deleteMessage, setDeleteMessage] = useState("Delete Successfully")

  const { register: register2, handleSubmit: handleSubmit2 } = useForm({
    mode: "onBlur",
  })
  const templateNameRegex = /^[A-Za-z&][A-Za-z-\s&]*$/

  const gettemplate = async () => {
    try {
      const response = await axios.get(`/api/v1/email-templates/`)
      settemplateOptions(
        response.data.map(cond => ({ name: cond.name, id: cond.template_id }))
      )
    } catch (error) {}
  }

  const handleSelectTemplate = async value => {
    setSelectedTemplate(value)
    try {
      const response = await axios.get(`/api/v1/email-templates/${value.id}/`)
      setTemplateContent(response.data.body)
    } catch (error) {
      setTemplateContent("")
    }
  }

  const onSubmittemplate = async data => {
    if (!templateNameRegex.test(newTemplateName)) {
      seterrorhandlingtem("Template name must start with a letter.")
      return
    }
    const req = { name: newTemplateName }
    if (!isEditedtem) {
      try {
        await axios.post(`/api/v1/email-templates/`, req)
        gettemplate()
        setNewTemplateVisible(false)
        setsmcreate("Added New Template")
        setSmShowcreate(true)
        setSelectedTemplate("")
      } catch (error) {
        if (error.response?.data?.error)
          seterrorhandlingtem(error.response.data.error)
      }
    } else {
      try {
        await axios.patch(`/api/v1/email-templates/${editIdtem}/`, req)
        setNewTemplateVisible(false)
        setsmcreate("Template Updated")
        setSmShowcreate(true)
        setnewTemplateName("")
        gettemplate()
        setSelectedTemplate("")
      } catch (error) {
        if (error.response?.data?.error)
          seterrorhandlingtem(error.response.data.error)
      }
    }
  }

  const handleSaveTemplate = async id => {
    const req = { name: selectedTemplate.name, body: templateContent }
    try {
      const response = await axios.patch(`/api/v1/email-templates/${id}/`, req)
      if ([200, 204].includes(response.status)) {
        setDisplayDialog(false)
        setsmcreate("Edited")
        setSmShowcreate(true)
        setTemplateContent("")
        setSelectedTemplate("")
      }
    } catch (error) {}
  }

  const handleDeleteTemplate = async id => {
    try {
      const response = await axios.delete(`/api/v1/email-templates/${id}/`)
      if ([200, 204].includes(response.status)) {
        gettemplate()
        setTemplateContent("")
        setSelectedTemplate("")
        setDeleteMessage("Template Deleted")
        setSuccessAlert(true)
      }
    } catch (error) {}
  }

  const closeAddDocumentDialog = () => {
    setDisplayDialog(false)
    setSmShow(false)
    setSuccessAlert(false)
  }

  useEffect(() => {
    let active = true
    Promise.all([getsignature(), gettemplate()]).finally(() => {
      if (active) setPageLoading(false)
    })
    return () => {
      active = false
    }
  }, [])

  const renderHeader = () => (
    <span className="ql-formats">
      <button className="ql-bold"></button>
      <button className="ql-italic"></button>
      <button className="ql-underline"></button>
      <select className="ql-font"></select>
      <select className="ql-size"></select>
      <select className="ql-color"></select>
      <select className="ql-background"></select>
      <button className="ql-image"></button>
      <button className="ql-link"></button>
      <select className="ql-align">
        <option value="" label="Left Align"></option>
        <option value="center" label="Center Align"></option>
        <option value="right" label="Right Align"></option>
        <option value="justify" label="Justify Align"></option>
      </select>
    </span>
  )
  const header = renderHeader()
  const templateHeader = renderHeader()

  const isSaveEnabled = isEditing
  const isAnyTemplateAdded = true
  const isTemplateSaveEnabled = isTemplateEditing

  const emailsignature = [{ label: "Settings" }, { label: "Email Signature" }]
  const emailtemplate = [{ label: "Settings" }, { label: "Email Template" }]
  const emailsetting = [{ label: "Settings" }, { label: "Email Settings" }]

  return (
    <React.Fragment>
      <Container fluid={true} className={embedded ? "pt-0" : "page-content"}>
        <div className={embedded ? "mb-4" : "settings-page-shell"}>
          <div className="container-fluid">
            <div className="settings-page-header">
              <div className="settings-page-header-icon">
                <i className="pi pi-cog"></i>
              </div>
              <div>
                <h1>Settings</h1>
                <p>Manage email settings, signatures, and templates.</p>
              </div>
            </div>
            {pageLoading ? (
              <div className="settings-page-loader" role="status" aria-live="polite">
                <span className="spinner-border text-primary" aria-hidden="true" />
                <span>Loading settings...</span>
              </div>
            ) : (
              <>
                {/* ── Home tiles ── */}
                {!selectedOption && (
                  <Row className="settings-option-grid">
                    <Col xl={4} md={6}>
                      <button type="button" onClick={() => setSelectedOption("interview-email-settings")} className="settings-option-card text-decoration-none w-100">
                        <span className="settings-option-icon"><i className="pi pi-calendar" /></span>
                        <span className="settings-option-title">Interview Email Settings</span>
                      </button>
                    </Col>
                    <Col xl={4} md={6}>
                      <div
                        className="settings-option-card"
                        onClick={() => setSelectedOption("email-settings")}
                      >
                        <span className="settings-option-icon">
                          <i className="pi pi-envelope"></i>
                        </span>
                        <span className="settings-option-title">Email Settings</span>
                      </div>
                    </Col>
                    <Col xl={4} md={6}>
                      <div
                        className="settings-option-card"
                        onClick={() => setSelectedOption("email-signature")}
                      >
                        <span className="settings-option-icon">
                          <i className="pi pi-pencil"></i>
                        </span>
                        <span className="settings-option-title">Email Signature</span>
                      </div>
                    </Col>
                    <Col xl={4} md={6}>
                      <div
                        className="settings-option-card"
                        onClick={() => setSelectedOption("email-template")}
                      >
                        <span className="settings-option-icon">
                          <i className="pi pi-file"></i>
                        </span>
                        <span className="settings-option-title">Email Template</span>
                      </div>
                    </Col>
                  </Row>
                )}

            {/* ── Email Signature ── */}
                {selectedOption === "interview-email-settings" && (
              <div>
                <button type="button" className="btn btn-link" onClick={() => setSelectedOption(null)}>Back to Settings</button>
                <InterviewEmailSettings embedded />
              </div>
            )}
                {selectedOption === "email-signature" && (
              <div>
                <div className="breadcrumb d-flex justify-content-between">
                  <BreadCrumb model={emailsignature} />
                  <button
                    className="p-button p-component"
                    onClick={() => {
                      setSelectSign("")
                      setText("")
                      setSelectedOption(null)
                    }}
                  >
                    <i className="pi pi-arrow-left me-2"></i>Back
                  </button>
                </div>
                <Row>
                  <Col lg={8}>
                    <div className="p-field me-2 mb-2 select-sign">
                      <label className="mb-2 mt-3">
                        Select Signature to edit
                      </label>
                      <Dropdown
                        value={selectSign}
                        options={signatureOptions}
                        onChange={e => handleSelectSignature(e.value)}
                        placeholder="Select a signature"
                        optionLabel="name"
                        className="w-full"
                      />
                    </div>
                    <div className="d-flex crudbtn">
                      <Button
                        color="primary"
                        className="btn btn-primary emailbtn d-block me-2"
                        onClick={() => handleDeleteSignature(selectSign.id)}
                      >
                        Delete
                      </Button>
                      <Button
                        color="primary"
                        className="btn btn-primary emailbtn d-block me-2"
                        onClick={() => {
                          setDialogAction("new")
                          setSignatureName("")
                          setDialogVisible(true)
                          setisEditedsig(false)
                          setsmcreate("Added New Signature")
                          reset1()
                          seterrorhandling("")
                        }}
                      >
                        New
                      </Button>
                      <Button
                        color="primary"
                        className="btn btn-primary emailbtn d-block me-2"
                        disabled={!isSaveEnabled}
                        onClick={() => {
                          if (selectSign) {
                            setSignatureName(selectSign.name || "")
                            seteditIdsig(selectSign.id)
                            handleSaveSignature(selectSign.id)
                            setsmcreate("Signature Updated")
                          }
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        className="btn btn-primary emailbtn d-block me-2"
                        disabled={!selectSign}
                        onClick={() => {
                          setDialogAction("rename")
                          setSignatureName(selectSign?.name || "")
                          setDialogVisible(true)
                          setisEditedsig(true)
                          seteditIdsig(selectSign.id)
                          seterrorhandling("")
                        }}
                      >
                        Rename
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={8}>
                    <label className="mb-2 mt-4">Edit Signature</label>
                    <Editor
                      value={text}
                      onTextChange={e => {
                        setText(e.htmlValue)
                        setIsEditing(true)
                      }}
                      style={{ height: "300px" }}
                      headerTemplate={header}
                    />
                  </Col>
                </Row>
                <Row>
                  <div className="p-field flex align-items-center mt-2">
                    <input
                      id="sigDefault"
                      type="checkbox"
                      className="me-2"
                      checked={sigchecked}
                      onChange={e => setsigchecked(e.target.checked)}
                    />
                    <label htmlFor="sigDefault" className="mb-0">
                      Is Default
                    </label>
                  </div>
                </Row>
                <Dialog
                  header={
                    dialogAction === "new"
                      ? "New Signature"
                      : "Rename Signature"
                  }
                  visible={dialogVisible}
                  onHide={() => setDialogVisible(false)}
                  style={{ width: "30vw" }}
                >
                  <form onSubmit={handleSubmit1(onSubmitsignature)}>
                    <div className="p-field mb-2">
                      <label>
                        {dialogAction === "new"
                          ? "Type a name for this signature:"
                          : "Rename this signature:"}
                        <span className="text-danger">*</span>
                      </label>
                      <InputText
                        value={signatureName}
                        onChange={e => {
                          setSignatureName(e.target.value)
                          seterrorhandling("")
                        }}
                        placeholder="Signature Name"
                        className="w-full"
                      />
                      <div className="p-field flex align-items-center mt-2">
                        <input
                          id="sigDefaultDialog"
                          type="checkbox"
                          className="me-2"
                          {...register1("isDefaultSIG")}
                        />
                        <label htmlFor="sigDefaultDialog" className="mb-0">
                          Is Default
                        </label>
                      </div>
                    </div>
                    <small className="form-text text-danger">
                      {errorhandling}
                    </small>
                    <div className="d-flex justify-content-end">
                      <Button
                        label="Ok"
                        icon="pi pi-check"
                        className="btn btn-primary me-2 btn-main"
                      />
                    </div>
                  </form>
                </Dialog>
              </div>
            )}

            {/* ── Email Template ── */}
                {selectedOption === "email-template" && (
              <div>
                <div className="breadcrumb d-flex justify-content-between">
                  <BreadCrumb model={emailtemplate} />
                  <button
                    className="p-button p-component mb-2"
                    onClick={() => {
                      setTemplateContent("")
                      setSelectedTemplate("")
                      setSelectedOption(null)
                    }}
                  >
                    <i className="pi pi-arrow-left me-2"></i>Back
                  </button>
                </div>
                <Row>
                  <Col lg={12}>
                    <div className="p-field me-2 mb-2">
                      <label className="mb-2 mt-3">
                        Select Template to edit
                      </label>
                      <Dropdown
                        value={selectedTemplate}
                        options={templateOptions}
                        onChange={e => handleSelectTemplate(e.value)}
                        placeholder="Select a template"
                        className="w-full"
                        optionLabel="name"
                        disabled={!isAnyTemplateAdded}
                      />
                    </div>
                    <div className="d-flex">
                      <Button
                        color="primary"
                        className="btn btn-primary emailbtn d-block me-2"
                        onClick={() =>
                          handleDeleteTemplate(selectedTemplate.id)
                        }
                      >
                        Delete
                      </Button>
                      <Button
                        color="primary"
                        className="btn btn-primary emailbtn d-block me-2"
                        onClick={() => {
                          setNewTemplateVisible(true)
                          setnewTemplateName("")
                          setDialogAction("new")
                          setsmcreate("Added New Template")
                          seterrorhandlingtem("")
                        }}
                      >
                        New
                      </Button>
                      <Button
                        color="primary"
                        className="btn btn-primary emailbtn d-block me-2"
                        disabled={!isTemplateSaveEnabled}
                        onClick={() => {
                          if (selectedTemplate) {
                            handleSaveTemplate(selectedTemplate.id)
                            setsmcreate("Template Updated")
                          }
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        color="primary"
                        className="btn btn-primary emailbtn d-block"
                        disabled={!selectedTemplate}
                        onClick={() => {
                          setnewTemplateName(selectedTemplate?.name || "")
                          setDialogAction("rename")
                          setNewTemplateVisible(true)
                          setisEditedtem(true)
                          seteditIdtem(selectedTemplate?.id)
                        }}
                      >
                        Rename
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    <label className="mb-2 mt-4">Edit Template</label>
                    <Editor
                      value={templateContent}
                      onTextChange={e => {
                        setTemplateContent(e.htmlValue)
                        setIsTemplateEditing(true)
                      }}
                      style={{ height: "360px" }}
                      headerTemplate={templateHeader}
                    />
                  </Col>
                </Row>
                <Dialog
                  header={
                    dialogAction === "new" ? "New Template" : "Rename Template"
                  }
                  visible={isNewTemplateVisible}
                  onHide={() => setNewTemplateVisible(false)}
                  style={{ width: "30vw" }}
                >
                  <form onSubmit={handleSubmit2(onSubmittemplate)}>
                    <div className="p-field mb-2">
                      <label>
                        Type a name for this template:
                        <span className="text-danger">*</span>
                      </label>
                      <InputText
                        id="newTemplateName"
                        value={newTemplateName}
                        onChange={e => {
                          setnewTemplateName(e.target.value)
                          seterrorhandlingtem("")
                        }}
                        placeholder="Template Name"
                        className="w-full"
                      />
                    </div>
                    <small className="form-text text-danger">
                      {errorhandlingtem}
                    </small>
                    <div className="d-flex justify-content-end">
                      <Button
                        label="Ok"
                        icon="pi pi-check"
                        className="btn btn-primary me-2 btn-main"
                        type="submit"
                      />
                    </div>
                  </form>
                </Dialog>
              </div>
            )}

            {/* ── Email Settings — TWO TABS ── */}
                {selectedOption === "email-settings" && (
              <div>
                {/* Tab Headers */}
                <div style={tabStyles.tabContainer}>
                  <button
                    style={tabStyles.tab(smtpTab === "user")}
                    onClick={() => setSmtpTab("user")}
                  >
                    <i className="pi pi-user me-2"></i>User Based SMTP
                  </button>
                  <button
                    style={tabStyles.tab(smtpTab === "company")}
                    onClick={() => setSmtpTab("company")}
                  >
                    <i className="pi pi-building me-2"></i>Company Based SMTP
                  </button>
                  <button
                    style={tabStyles.tab(smtpTab === "vitel")}
                    onClick={() => setSmtpTab("vitel")}
                  >
                    <i className="pi pi-video me-2"></i>Vitel Meet Setup
                  </button>
                </div>

                {/* ══════════════════════════════════════════════
                    USER BASED SMTP TAB
                    CompanySelector filters the table AND passes
                    company_id to POST/PATCH requests.
                ══════════════════════════════════════════════ */}
                {smtpTab === "user" && (
                  <div>
                    {/* Company filter bar — top-right, matching the provided snippet */}
                    <div className="mt-2">
                      <div className="d-flex justify-content-end">
                        <label className="mt-2">Select Company</label>
                        <div className="mb-2 mx-2" style={{ width: "170px" }}>
                          <CompanySelector
                            showAllOption
                            allOptionValue="all"
                            onCompanyChange={c => {
                              const nextCompanyId = String(c?.company_id || "").toLowerCase() === "all"
                                ? "all"
                                : c?.company_id ?? null
                              dispatch(setPageData({ first: 0, rows, totalRecords: 0 }))
                              setUserTabCompanyId(nextCompanyId)
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <SmtpForm
                      mode="user"
                      smtpAccounts={userSmtpAccounts}
                      selectedCompanyId={userTabCompanyId}
                      loading={loading}
                      first={first}
                      rows={rows}
                      totalRecords={totalRecords}
                      dispatch={dispatch}
                      getAllActive={(results, count) => {
                        setUserSmtpAccounts(results)
                        setUserSmtpTotal(count)
                      }}
                      type={type}
                      atsUserOptions={atsUserOptions}
                      isAtsUsersLoading={isAtsUsersLoading}
                      companyOptions={[]}
                      isCompaniesLoading={false}
                      onBack={() => setSelectedOption(null)}
                    />
                  </div>
                )}

                {/* ══════════════════════════════════════════════
                    COMPANY BASED SMTP TAB
                    CompanySelector filters the table AND is the
                    primary company_id sent to POST/PATCH.
                ══════════════════════════════════════════════ */}
                {smtpTab === "company" && (
                  <div>
                    {/* Company filter bar */}
                    <div className="mt-2">
                      <div className="d-flex justify-content-end">
                        <label className="mt-2">Select Company</label>
                        <div className="mb-2 mx-2" style={{ width: "170px" }}>
                          <CompanySelector
                            showAllOption
                            allOptionValue="all"
                            onCompanyChange={c => {
                              const nextCompanyId = String(c?.company_id || "").toLowerCase() === "all"
                                ? "all"
                                : c?.company_id ?? null
                              dispatch(setPageData({ first: 0, rows, totalRecords: 0 }))
                              setCompanyTabCompanyId(nextCompanyId)
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <SmtpForm
                      mode="company"
                      smtpAccounts={companySmtpAccounts}
                      selectedCompanyId={companyTabCompanyId}
                      loading={loading}
                      first={first}
                      rows={rows}
                      totalRecords={companySmtpTotal}
                      dispatch={dispatch}
                      getAllActive={(results, count) => {
                        setCompanySmtpAccounts(results)
                        setCompanySmtpTotal(count)
                      }}
                      type={type}
                      atsUserOptions={[]}
                      isAtsUsersLoading={false}
                      companyOptions={companyOptions}
                      isCompaniesLoading={isCompaniesLoading}
                      onBack={() => setSelectedOption(null)}
                    />
                  </div>
                )}
                {smtpTab === "vitel" && (
                  <div>
                    <div className="mt-2">
                      <div className="d-flex justify-content-end">
                        <label className="mt-2">Select Company</label>
                        <div className="mb-2 mx-2" style={{ width: "170px" }}>
                          <CompanySelector
                            showAllOption
                            allOptionValue="all"
                            onCompanyChange={c => {
                              const nextCompanyId = String(c?.company_id || "").toLowerCase() === "all"
                                ? "all"
                                : c?.company_id ?? null
                              dispatch(setPageData({ first: 0, rows, totalRecords: 0 }))
                              setCompanyTabCompanyIdVitel(nextCompanyId)
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <VitelMeetForm
                      selectedCompanyId={companyTabCompanyIdVitel}
                      atsUserOptions={atsUserOptions}
                      isAtsUsersLoading={isAtsUsersLoading}
                      fetchAtsUsers={fetchAtsUsers}
                      onBack={() => setSelectedOption(null)}
                    />
                  </div>
                )}
              </div>
                )}
              </>
            )}
          </div>
        </div>
      </Container>

      {/* Global Signature Modals */}
      <Modal
        size="md"
        show={smShowcreate}
        onHide={() => setSmShowcreate(false)}
        className="text-center settings-success-modal"
      >
        <Modal.Header closeButton>
          <h1 className="modal-title fs-5">{smcreate}</h1>
        </Modal.Header>
        <h4 className="text-center m-2">{smcreate} Successfully</h4>
        <div className="text-center m-3">
          <Button
            className="btn btn-primary m-1"
            onClick={() => setSmShowcreate(false)}
          >
            OK
          </Button>
        </div>
      </Modal>

      <Modal
        size="md"
        show={smShowDeleteSig}
        onHide={() => setSmShowDeleteSig(false)}
        className="text-center"
      >
        <Modal.Header closeButton>
          <h1 className="modal-title fs-5">{smDeleteSig}</h1>
        </Modal.Header>
        <h4 className="text-center m-2">{smDeleteSig} Successfully</h4>
        <div className="text-center m-3">
          <Button
            className="btn btn-primary m-1"
            onClick={() => setSmShowDeleteSig(false)}
          >
            OK
          </Button>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default Settings
